import { ObservableProp, useClassStore } from '@Utils/useClassStore';
import { inject, injectable, postConstruct } from 'inversify';
import { computed, reaction } from 'mobx';

import container from '~ioc/ioc';
import { AnalyticsStore } from '~stores/analytics';
import AuthStore from '~stores/authentication';
import { NavigationStore } from '~stores/navigation';
import type { AbandonCheckoutReason2, CheckoutStep } from '~types/analytics';

@injectable()
export class CheckoutContextState {
  @inject(NavigationStore) private _navigator2Store!: NavigationStore;
  @inject(AnalyticsStore) private _analyticsStore!: AnalyticsStore;
  @inject(AuthStore) private _authStore!: AuthStore;

  orderSummaryVisible = new ObservableProp(false);
  guestOrLoginPromptVisible = new ObservableProp(false);
  loginDetailsVisible = new ObservableProp(false);
  orderDetailsVisible = new ObservableProp(false);
  orderConfirmVisible = new ObservableProp(false);

  checkoutAbandonReason?: AbandonCheckoutReason2;

  @postConstruct() init() {
    reaction(
      () => this._navigator2Store.activeProductId,
      () => {
        this.monitorProductId();
      },
      { fireImmediately: true }
    );

    reaction(
      () => this.allDisabled,
      () => {
        if (this.allDisabled) {
          // if Buy flow is closed then reset abandon reason
          this.checkoutAbandonReason = undefined;
        }
      }
    );
  }

  @computed get currentStep(): CheckoutStep {
    if (this.allDisabled) {
      return 'none'; // checkout panel is closed
    }

    if (this.orderConfirmVisible.value) {
      return 'post_order';
    } // reviewing after order went-through
    if (this.orderDetailsVisible.value) {
      return 'ordering';
    } // pickup ordering addresses and payment methods
    if (this.loginDetailsVisible.value) {
      return 'logging_in';
    }
    if (this.guestOrLoginPromptVisible.value) {
      return 'identify_prompt';
    } // prompted to sign-in or go guest mode

    return 'preview_summary'; // just started and previewing prices and quantity
  }

  @computed private get allDisabled(): boolean {
    return (
      !this.orderSummaryVisible.value &&
      !this.guestOrLoginPromptVisible.value &&
      !this.loginDetailsVisible.value &&
      !this.orderDetailsVisible.value &&
      !this.orderConfirmVisible.value
    );
  }

  onBuyPress = () => {
    this.checkoutAbandonReason = undefined;
  };

  onCloseButton = () => {
    this.checkoutAbandonReason = 'close_window';
    this.logAbandonCheckoutAnalytics();
  };

  onCancelButton = () => {
    this.checkoutAbandonReason = 'cancel_button';
    this.logAbandonCheckoutAnalytics();
  };

  private monitorProductId = () => {
    const newId = this._navigator2Store.activeProductId;

    if (this.currentStep === 'none') {
      // no checkout session started so no interaction needed
      return;
    }

    if (
      this.checkoutAbandonReason !== undefined &&
      this.checkoutAbandonReason !== 'product_change'
    ) {
      // something else cancelled the checkout or checkout completed
      return;
    }

    if (newId === undefined) {
      // journey changed
      this.checkoutAbandonReason = 'journey_change';
      this.logAbandonCheckoutAnalytics();
      return;
    }

    this.checkoutAbandonReason = 'product_change';
    this.logAbandonCheckoutAnalytics();
  };

  private logAbandonCheckoutAnalytics = () => {
    this._analyticsStore.logAbandonCheckout({
      userId: this._authStore.currentUser?.id,
      step: this.currentStep,
      reason: this.checkoutAbandonReason!,
    });
  };
}

export const useCheckoutContextState = () =>
  useClassStore<CheckoutContextState>(container.get(CheckoutContextState));
